import type { RequestStatus } from '@skynet/data-ws-core';
import type { JsonExchange } from '@skynet/data-shared-exchanges';
import { isRecord } from '@skynet/type-guards';
import type {
  AccountRow,
  ApiKeyEntry,
  ApiKeyRow,
  EntityRow,
  ExchangeSupportedOperations,
  JsonAccount,
  JsonSubAccountAsset,
  MainAccount,
  OperationFlags,
  SubAccount,
  SubAccountAsset,
  SubAccountRow,
  SubAccountsFilters,
  SupportedOperationItem,
  SupportedOperationsResponse,
} from './types';

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

function toAsset({ currency, amount, price, quotePrice }: JsonSubAccountAsset): SubAccountAsset {
  const priceUSD = price * quotePrice;
  return { token: currency, volume: amount, priceUSD, volumeUSD: amount * priceUSD };
}

/**
 * Normalises an account of the `MainAccounts` or `SubAccounts` frame: its
 * keys and balances always present, the balances priced in USD.
 */
export function normalizeAccount({
  id,
  exchangeID,
  name,
  uid,
  note,
  apiKeys,
  assets,
}: JsonAccount): MainAccount {
  return {
    id,
    exchangeID,
    name,
    uid,
    note,
    apiKeys: Array.isArray(apiKeys) ? apiKeys : [],
    assets: (Array.isArray(assets) ? assets : []).map(toAsset),
  };
}

// ---------------------------------------------------------------------------
// Supported operations
// ---------------------------------------------------------------------------

/**
 * The flags of one exchange, every one of them present. Fails closed: an item
 * kind or a flag the backend left out, or sent as anything but `true`, reads
 * as unsupported.
 */
function toExchangeOperations(entry: unknown): ExchangeSupportedOperations {
  const flagsOf = (item: SupportedOperationItem): OperationFlags => {
    const flags = isRecord(entry) ? entry[item] : undefined;
    const isOn = (operation: keyof OperationFlags) => isRecord(flags) && flags[operation] === true;
    return { create: isOn('create'), delete: isOn('delete'), rename: isOn('rename') };
  };
  return {
    mainAccounts: flagsOf('mainAccounts'),
    mainAccountApiKeys: flagsOf('mainAccountApiKeys'),
    subAccounts: flagsOf('subAccounts'),
    subAccountApiKeys: flagsOf('subAccountApiKeys'),
  };
}

/** Normalises the `SupportedOperations` frame: the flags of every exchange, by exchange id. */
export function normalizeSupportedOperations(
  json: SupportedOperationsResponse,
): Record<string, ExchangeSupportedOperations> {
  const byExchange: Record<string, ExchangeSupportedOperations> = {};
  for (const [exchangeID, entry] of Object.entries(json.perExchange)) {
    byExchange[exchangeID] = toExchangeOperations(entry);
  }
  return byExchange;
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

function toSearchTokens(search: string): string[] {
  return search
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 0);
}

/**
 * Whether every word of the search is part of at least one of the fields,
 * case-insensitively. The words may be spread over the fields, so "mexc 2347"
 * matches an account named "MEXC …" with uid "2347…".
 */
function matchesSearch(tokens: string[], ...fields: (string | null | undefined)[]): boolean {
  const texts = fields.filter((text): text is string => Boolean(text)).map((t) => t.toLowerCase());
  return texts.length > 0 && tokens.every((t) => texts.some((text) => text.includes(t)));
}

/**
 * Whether a token's symbol answers the search box: some word of the search is
 * part of it, case-insensitively — so in "mexc btc" the BTC balance still
 * stands out. An empty search matches nothing, so no token is singled out.
 */
export function matchesTokenSearch(token: string, search: string): boolean {
  const lower = token.toLowerCase();
  return toSearchTokens(search).some((t) => lower.includes(t));
}

/** Symbols of the tokens an entity actually holds (non-zero balances). */
function heldSymbols(assets: readonly SubAccountAsset[] | undefined): string[] {
  return (assets ?? []).filter((a) => a.volume !== 0).map((a) => a.token);
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

interface BuildContext {
  tokens: string[];
  subAccounts: Record<string, SubAccount[]>;
  subAccountsStatus: Record<string, RequestStatus>;
  /** Disambiguates a row key when the backend repeats an id (see {@link dedupeKey}). */
  seenKeys: Map<string, number>;
}

/**
 * Tree libraries (here `SkyResponsiveTree`/antd `Tree`) track which rows are
 * expanded by this key alone: two sibling rows sharing one key share that
 * expanded state too, so toggling one toggles both. The backend is expected
 * to hand out unique ids, but a duplicate should degrade into two
 * independently-toggleable rows, not a rendering glitch — so a repeat of
 * `base` gets a numbered suffix.
 */
function dedupeKey(seenKeys: Map<string, number>, base: string): string {
  const seen = seenKeys.get(base) ?? 0;
  seenKeys.set(base, seen + 1);
  return seen === 0 ? base : `${base}:${seen}`;
}

function buildKeyRow(
  entry: ApiKeyEntry,
  exchangeID: number,
  ownerLevel: ApiKeyRow['ownerLevel'],
  ctx: BuildContext,
): ApiKeyRow {
  return {
    key: dedupeKey(ctx.seenKeys, `apiKey:${entry.id}`),
    level: 'apiKey',
    label: entry.name,
    exchangeID,
    data: entry,
    ownerLevel,
  };
}

/**
 * Key rows of an account or sub-account. When the owner matched the search
 * every key is kept, otherwise only the matching ones.
 */
function buildKeyRows(
  owner: MainAccount | SubAccount,
  ownerLevel: ApiKeyRow['ownerLevel'],
  ownerMatches: boolean,
  ctx: BuildContext,
): ApiKeyRow[] {
  const keys = owner.apiKeys;
  const visible =
    ctx.tokens.length === 0 || ownerMatches
      ? keys
      : // The owner's name counts too: "binance key1" finds key1 of a Binance account.
        keys.filter((k) => matchesSearch(ctx.tokens, k.name, k.key, owner.name, owner.uid));
  return visible.map((k) => buildKeyRow(k, owner.exchangeID, ownerLevel, ctx));
}

/**
 * The row of one main account, with its keys and then its sub-accounts (each
 * with its keys) as children.
 *
 * Search keeps an entity when every word of it is found in the entity — its
 * name, uid, or a held token's symbol — or in the account it belongs to; when
 * its owner matches by name (a matching account brings everything under it);
 * or when something under it matches (so a matching key or token holder is
 * shown with the accounts it belongs to). Sub-accounts not loaded yet cannot
 * match: the search covers only what the page has.
 */
function buildAccountRow(acc: MainAccount, ctx: BuildContext): AccountRow | null {
  const hasSearch = ctx.tokens.length > 0;
  const accMatches = !hasSearch || matchesSearch(ctx.tokens, acc.name, acc.uid);
  const accAssets = acc.assets;
  // Holding a searched token keeps the account itself, but — unlike a name
  // match — not everything under it: only sub-accounts that match on their own.
  const accHoldsToken =
    hasSearch && matchesSearch(ctx.tokens, acc.name, acc.uid, ...heldSymbols(accAssets));

  const loadedSubs = ctx.subAccounts[String(acc.id)];
  const subRows = (loadedSubs ?? []).flatMap((sub): SubAccountRow[] => {
    const subAssets = sub.assets;
    const subMatches =
      accMatches ||
      matchesSearch(ctx.tokens, sub.name, sub.uid, acc.name, acc.uid, ...heldSymbols(subAssets));
    const children = buildKeyRows(sub, 'subAccount', subMatches, ctx);
    if (!subMatches && children.length === 0) return [];

    return [
      {
        key: dedupeKey(ctx.seenKeys, `subAccount:${sub.id}`),
        level: 'subAccount',
        label: sub.name,
        exchangeID: sub.exchangeID,
        data: sub,
        assets: subAssets,
        children,
      },
    ];
  });

  const keyRows = buildKeyRows(acc, 'account', accMatches || accHoldsToken, ctx);
  if (!accMatches && !accHoldsToken && keyRows.length === 0 && subRows.length === 0) return null;

  return {
    key: dedupeKey(ctx.seenKeys, `account:${acc.id}`),
    level: 'account',
    label: acc.name,
    exchangeID: acc.exchangeID,
    data: acc,
    assets: accAssets,
    apiKeyLabels: acc.apiKeys.map((k) => k.name),
    subAccountsStatus: ctx.subAccountsStatus[String(acc.id)] ?? null,
    subAccountsLoaded: loadedSubs !== undefined,
    children: [...keyRows, ...subRows],
  };
}

function groupBy<T>(items: T[], getKey: (item: T) => number): Map<number, T[]> {
  const map = new Map<number, T[]>();
  for (const item of items) {
    const key = getKey(item);
    const list = map.get(key);
    if (list) list.push(item);
    else map.set(key, [item]);
  }
  return map;
}

export interface BuildAccountRowsParams {
  exchanges: JsonExchange[];
  mainAccounts: MainAccount[];
  /** Loaded sub-accounts, by main account id. */
  subAccounts: Record<string, SubAccount[]>;
  /** Progress of each main account's `SubAccounts` request, by its id. */
  subAccountsStatus: Record<string, RequestStatus>;
  filters: SubAccountsFilters;
}

/**
 * Turns the main accounts, and the sub-accounts loaded so far, into one tree
 * row per main account, listed in exchange order. Accounts on an exchange missing from the
 * exchange list (e.g. not loaded yet) follow, in their own order.
 *
 * Accounts on exchanges excluded by the exchange filter, and accounts left
 * empty by the search, are dropped.
 */
export function buildAccountRows({
  exchanges,
  mainAccounts,
  subAccounts,
  subAccountsStatus,
  filters,
}: BuildAccountRowsParams): AccountRow[] {
  const ctx: BuildContext = {
    tokens: toSearchTokens(filters.search),
    subAccounts,
    subAccountsStatus,
    seenKeys: new Map(),
  };

  const exchangeFilter = new Set(filters.exchanges);
  const isShown = (exchangeID: number) =>
    exchangeFilter.size === 0 || exchangeFilter.has(exchangeID);
  const accountsByExchange = groupBy(mainAccounts, (a) => a.exchangeID);
  const knownExchanges = new Set(exchanges.map((exchange) => exchange.id));

  const ordered = [
    ...exchanges.flatMap((exchange) => accountsByExchange.get(exchange.id) ?? []),
    ...mainAccounts.filter((acc) => !knownExchanges.has(acc.exchangeID)),
  ];

  return ordered
    .filter((acc) => isShown(acc.exchangeID))
    .map((acc) => buildAccountRow(acc, ctx))
    .filter((row): row is AccountRow => row !== null);
}

/** Every account, sub-account and API key row, depth-first in display order. */
export function flattenEntities(rows: readonly EntityRow[]): EntityRow[] {
  const out: EntityRow[] = [];
  const visit = (row: EntityRow) => {
    out.push(row);
    row.children?.forEach(visit);
  };
  rows.forEach(visit);
  return out;
}

/** Counts account, sub-account and API key rows at every depth. */
export function countEntityRows(rows: readonly EntityRow[]): number {
  return flattenEntities(rows).length;
}
